import { AUTOPILOT_RECHECK_LICENSE } from "components/licenses/bundles";
import { BLANCCO_TOKEN_ID } from "components/licenses/common";
import { deduceTierLicenses } from "components/tenants/add-tenant/AddTenantForm";
import { LicensingModel } from "domain/tenants";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { licenseService } from "services/licenses/LicenseService";
import { getCurrentTenantDetails } from "services/tenants/tenantCookieService";
import { TenantDetails } from "store/tenantDetails";
import { logger } from "utils/logging";

export interface AutopilotDeviceIdentifier {
    systemUuid?: string;
    offlineDeviceId?: string;
    reportUuid: string;
}

export interface ConsumedLicense {
    id: string;
    amount: number;
    failed_devices: string[];
}

export interface RecheckResponse {
    consumedLicenses: ConsumedLicense[];
}

interface RecheckResponseDto {
    consumed_licenses: ConsumedLicense[];
}

interface RecheckRequestDto {
    devices: {
        system_uuid?: string;
        offline_device_id?: string;
        report_uuid: string;
    }[];
}

class AutopilotService {
    private hasValidLicenses: boolean = false;
    private lastCheck: number = 0;
    private cacheTimeout: number = 60 * 1000;

    public recheck(identifiers: AutopilotDeviceIdentifier[], dryRun: boolean = true): Promise<RecheckResponse> {
        const body: RecheckRequestDto = {
            devices: identifiers.map((e) => ({
                system_uuid: e.systemUuid,
                offline_device_id: e.offlineDeviceId,
                report_uuid: e.reportUuid,
            })),
        };
        return apiGatewayService
            .invokeApi(`/api/v1/autopilot/recheck?dry_run=` + dryRun, "POST", body, { apiType: ApiType.OLIVER })
            .then((dto: RecheckResponseDto) => ({
                consumedLicenses: dto.consumed_licenses,
            }));
    }

    private isRecheckIncludedInBundle(tenant: TenantDetails) {
        const availableProducts = deduceTierLicenses(tenant.tenantTier, false, true);
        if (availableProducts == null || availableProducts.length === 0) {
            return false;
        }
        return availableProducts.includes(AUTOPILOT_RECHECK_LICENSE);
    }

    public async fetchAvailableLicenses() {
        const tenant = getCurrentTenantDetails();
        const isTokenModel = tenant.licensingModel === LicensingModel.BUNDLE_WITH_TOKEN;
        const productId =
            isTokenModel && this.isRecheckIncludedInBundle(tenant) ? BLANCCO_TOKEN_ID : AUTOPILOT_RECHECK_LICENSE;
        const data = await licenseService.fetchAvailableLicenses(productId);

        if (isTokenModel) {
            const rate =
                (await licenseService.fetchTokenRateForTenantAndProduct(
                    new AbortController(),
                    AUTOPILOT_RECHECK_LICENSE,
                    tenant.uuid
                )) || -1;
            const availableLicenses = rate < 0 ? 0 : data.availableLicenses / 100 / rate;

            if (rate < 0) {
                logger.error("Failed to fetch token rate for autopilot recheck license");
            }
            return {
                availableLicenses: availableLicenses,
                expiration: data.expiration,
            };
        }

        return {
            availableLicenses: data.availableLicenses,
            expiration: data.expiration,
        };
    }

    public async updateLicenseStatus(): Promise<void> {
        const data = await this.fetchAvailableLicenses();
        const isNotExpired = !data.expiration || new Date(data.expiration) > new Date();
        this.hasValidLicenses = data.availableLicenses > 0 && isNotExpired;
    }

    public async hasValidLicensesForRecheck(): Promise<boolean> {
        const now = Date.now();
        if (now - this.lastCheck > this.cacheTimeout) {
            this.lastCheck = now;
            await this.updateLicenseStatus();
        }
        return this.hasValidLicenses;
    }
}

export const autopilotService = new AutopilotService();
